import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, Calendar, X } from "lucide-react";
import { colors, announcementBar } from "../config/site";

/**
 * Sticky top announcement / "Get Help Today" CTA bar.
 * - Renders only if announcementBar.enabled === true in site config.
 * - Dismissible per browser session (sessionStorage). No tracking.
 * - Reports its own height to a CSS var (`--announcement-h`) so the Header can sit below it.
 */
const AnnouncementBar = () => {
  const initialDismissed =
    typeof window !== "undefined" &&
    window.sessionStorage?.getItem("dth-announcement-dismissed") === "1";
  const [dismissed, setDismissed] = useState(initialDismissed);
  const ref = useRef(null);

  // Keep --announcement-h in sync with this bar's actual height
  useEffect(() => {
    if (!announcementBar.enabled || dismissed) {
      document.documentElement.style.setProperty("--announcement-h", "0px");
      return;
    }
    const setHeight = () => {
      if (ref.current) {
        const h = ref.current.offsetHeight;
        document.documentElement.style.setProperty("--announcement-h", `${h}px`);
      }
    };
    setHeight();
    window.addEventListener("resize", setHeight);
    return () => window.removeEventListener("resize", setHeight);
  }, [dismissed]);

  if (!announcementBar.enabled || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage?.setItem("dth-announcement-dismissed", "1");
    } catch (e) {
      /* sessionStorage may be unavailable; safe to ignore */
    }
  };

  return (
    <div
      ref={ref}
      data-testid="announcement-bar"
      className="fixed top-0 left-0 right-0 z-[60] text-white print:hidden"
      style={{
        background: `linear-gradient(90deg, ${colors.primaryDark}, ${colors.primary} 60%, ${colors.terracottaDark})`,
      }}
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-center sm:justify-between gap-3 flex-wrap">
          <p className="text-xs sm:text-sm font-medium text-center sm:text-left flex-1 min-w-0">
            {announcementBar.text}
          </p>
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <a
              href={announcementBar.ctaHref}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-transform hover:scale-105"
              style={{ backgroundColor: colors.gold, color: colors.charcoal }}
              data-testid="announcement-call-cta"
            >
              <Phone className="w-3.5 h-3.5" />
              {announcementBar.ctaLabel}
            </a>
            <Link
              to={announcementBar.secondaryHref}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold border border-white/40 hover:bg-white/10 transition-colors"
              data-testid="announcement-consult-cta"
            >
              <Calendar className="w-3.5 h-3.5" />
              {announcementBar.secondaryLabel}
            </Link>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss announcement"
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
              data-testid="announcement-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBar;
